import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getExpenseDetail,
  getExpenseActivityLog,
  submitExpense,
  approveExpense,
  rejectExpense,
  returnExpense,
} from "../../../../services/workspace/reimbursement.service";
import { toast } from "react-hot-toast";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import DefaultButton from "../../../components/buttons/defaultButton";
import { FiArrowLeft, FiDownload, FiClock, FiUser, FiCheck, FiCheckCircle, FiX, FiRotateCcw } from "react-icons/fi";
import DefaultTextarea from "../../../components/inputs/defaultTextarea";
import CommonModal from "../../../components/modals/commonModel";
import moment from "moment";

const statusConfig = {
  DRAFT: { label: "Draft", color: "gray" },
  SUBMITTED: { label: "Submitted", color: "blue" },
  PENDING_APPROVAL: { label: "Pending Approval", color: "yellow" },
  APPROVED: { label: "Approved", color: "green" },
  REJECTED: { label: "Rejected", color: "red" },
  RETURNED: { label: "Returned", color: "orange" },
  PROCESSING: { label: "Processing", color: "indigo" },
  PAID: { label: "Paid", color: "emerald" },
  ADDED_TO_PAYROLL: { label: "Added to Payroll", color: "purple" },
  INSTANT_PAYOUT: { label: "Instant Payout", color: "green" },
  PAYROLL: { label: "Payroll", color: "purple" },
};

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [expense, setExpense] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionModal, setActionModal] = useState({
    show: false,
    type: null, // 'approve', 'reject', 'return'
    notes: "",
  });
  
  // Determine source page from location state or referrer
  const sourcePage = location.state?.from || 
    (document.referrer.includes('manager-approvals') ? 'manager-approvals' : 'my-expenses');

  useEffect(() => {
    fetchExpenseDetail();
    fetchActivityLog();
  }, [id]);

  const fetchExpenseDetail = async () => {
    try {
      const response = await getExpenseDetail(id);
      // Handle different response structures
      const expenseData = response?.data || response;
      if (process.env.NODE_ENV === 'development') {
      }
      setExpense(expenseData);
    } catch (error) {
      console.error("Failed to fetch expense details:", error);
      toast.error("Failed to fetch expense details");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const response = await getExpenseActivityLog(id);
      setActivityLog(response.data);
    } catch (error) {
      console.error("Failed to fetch activity log:", error);
    }
  };

  const handleSubmitExpense = async () => {
    if (!expense || !id) return;
    
    setSubmitting(true);
    try {
      await submitExpense(id);
      toast.success("Expense submitted for approval successfully!");
      // Refresh expense data and activity log
      await fetchExpenseDetail();
      await fetchActivityLog();
    } catch (error) {
      console.error("Failed to submit expense:", error);
      toast.error(error?.response?.data?.message || "Failed to submit expense for approval");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!expense || !id) return;
    
    setActionLoading(true);
    try {
      await approveExpense(id, { notes: actionModal.notes });
      toast.success("Expense approved successfully!");
      setActionModal({ show: false, type: null, notes: "" });
      await fetchExpenseDetail();
      await fetchActivityLog();
    } catch (error) {
      console.error("Failed to approve expense:", error);
      toast.error(error?.response?.data?.message || "Failed to approve expense");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!expense || !id) return;
    
    if (!actionModal.notes.trim()) {
      toast.error("Please provide rejection reason");
      return;
    }
    
    setActionLoading(true);
    try {
      await rejectExpense(id, { notes: actionModal.notes });
      toast.success("Expense rejected!");
      setActionModal({ show: false, type: null, notes: "" });
      await fetchExpenseDetail();
      await fetchActivityLog();
    } catch (error) {
      console.error("Failed to reject expense:", error);
      toast.error(error?.response?.data?.message || "Failed to reject expense");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturn = async () => {
    if (!expense || !id) return;
    
    if (!actionModal.notes.trim()) {
      toast.error("Please specify what needs clarification");
      return;
    }
    
    setActionLoading(true);
    try {
      await returnExpense(id, { notes: actionModal.notes });
      toast.success("Expense returned for clarification!");
      setActionModal({ show: false, type: null, notes: "" });
      await fetchExpenseDetail();
      await fetchActivityLog();
    } catch (error) {
      console.error("Failed to return expense:", error);
      toast.error(error?.response?.data?.message || "Failed to return expense");
    } finally {
      setActionLoading(false);
    }
  };

  const handleActionConfirm = () => {
    if (actionModal.type === 'approve') {
      handleApprove();
    } else if (actionModal.type === 'reject') {
      handleReject();
    } else if (actionModal.type === 'return') {
      handleReturn();
    }
  };

  const items = [
    { label: "Reimbursement", path: "/reimbursement/my-expenses" },
    {
      label: expense?.expense_number || "Expense Detail",
      path: "",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <p className="text-gray-600">Expense not found</p>
        <DefaultButton
          name="Go Back"
          variant="outline"
          onClick={() => navigate(-1)}
        />
      </div>
    );
  }

  // Normalize status to uppercase to match statusConfig keys
  // Handle different possible status field names
  const statusValue = expense.status || expense.status_name || expense.current_status || "DRAFT";
  const normalizedStatus = statusValue ? String(statusValue).toUpperCase().trim() : "DRAFT";
  const currentStatus = statusConfig[normalizedStatus] || statusConfig.DRAFT;
  
  // Map status colors to Tailwind classes for custom badge
  const colorMap = {
    gray: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300" },
    blue: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" },
    yellow: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300" },
    green: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300" },
    red: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300" },
    orange: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300" },
    indigo: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300" },
    emerald: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300" },
    purple: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300" },
  };
  
  const badgeStyles = colorMap[currentStatus.color] || colorMap.gray;

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader breadcrumbs={items} />

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiArrowLeft className="text-lg" />
            </button>
            <div>
              <h1 className="text-md font-bold text-gray-900">
                {expense.expense_number}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Submitted by {expense.employee?.name}
              </p>
            </div>
          </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-semibold shadow-sm ${badgeStyles.bg} ${badgeStyles.text} ${badgeStyles.border}`}
          >
            {currentStatus.label || normalizedStatus || "Unknown"}
          </span>
          
          {/* Action Buttons based on source and status */}
          {sourcePage === 'my-expenses' && normalizedStatus === "DRAFT" && (
            <DefaultButton
              name="Submit for Approval"
              size="md"
              onClick={handleSubmitExpense}
              loading={submitting}
              disabled={submitting}
              minWidth
            />
          )}
          
          {/* Manager Actions - Only show if from manager-approvals and status is PENDING_APPROVAL */}
          {sourcePage === 'manager-approvals' && normalizedStatus === "PENDING_APPROVAL" && (
            <div className="flex items-center gap-2">
              <DefaultButton
                name="Approve"
                variant="success"
                size="md"
                icon={<FiCheck />}
                onClick={() => setActionModal({ show: true, type: 'approve', notes: "" })}
                disabled={actionLoading}
                classnames="min-w-24"
              />
              <DefaultButton
                name="Reject"
                variant="danger"
                size="md"
                icon={<FiX />}
                onClick={() => setActionModal({ show: true, type: 'reject', notes: "" })}
                disabled={actionLoading}
                minWidth
              />
              <DefaultButton
                name="Return"
                variant="warning"
                size="md"
                icon={<FiRotateCcw />}
                onClick={() => setActionModal({ show: true, type: 'return', notes: "" })}
                disabled={actionLoading}
                minWidth
              />
            </div>
          )}
        </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Amount</p>
            <p className="text-md font-bold text-gray-900">
              ₹{expense.amount?.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Category</p>
            <p className="text-md font-semibold text-gray-900">
              {expense.category?.name}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Expense Date</p>
            <p className="text-lg font-semibold text-gray-900">
              {moment(expense.expense_date).format("DD MMM YYYY")}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Vendor</p>
            <p className="text-lg font-semibold text-gray-900">
              {expense.vendor}
            </p>
          </div>
        </div>

        {/* Expense Details */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Expense Information
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Description</p>
                <p className="text-base text-gray-900 mt-1">
                  {expense.description}
                </p>
              </div>
              {expense.notes && (
                <div>
                  <p className="text-sm text-gray-600">Additional Notes</p>
                  <p className="text-base text-gray-900 mt-1">{expense.notes}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Department</p>
                <p className="text-base text-gray-900 mt-1">
                  {expense.employee?.department?.name || "-"}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Receipt
            </h3>
            {expense.receipt_url ? (
              <div className="border rounded-lg overflow-hidden">
                <img
                  src={expense.receipt_url}
                  alt="Receipt"
                  className="w-full h-64 object-contain bg-gray-50"
                />
                <div className="p-3 bg-gray-50 border-t">
                  <DefaultButton
                    name="Download Receipt"
                    variant="outline"
                    size="sm"
                    icon={<FiDownload />}
                    onClick={() => window.open(expense.receipt_url, "_blank")}
                  />
                </div>
              </div>
            ) : (
              <div className="border rounded-lg p-8 text-center text-gray-400">
                No receipt uploaded
              </div>
            )}
          </div>
        </div>

        {/* OCR Data */}
        {expense.ocr_data && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">
              OCR Extraction Data
            </h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-blue-700">Extracted Amount:</p>
                <p className="font-semibold text-blue-900">
                  ₹{expense.ocr_data.amount}
                </p>
              </div>
              <div>
                <p className="text-blue-700">Confidence Score:</p>
                <p className="font-semibold text-blue-900">
                  {(expense.ocr_data.confidence_score * 100).toFixed(0)}%
                </p>
              </div>
              <div>
                <p className="text-blue-700">Extracted Date:</p>
                <p className="font-semibold text-blue-900">
                  {expense.ocr_data.date || "-"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Policy Check */}
        {expense.exceeds_policy_limit && (
          <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="font-semibold text-orange-900">
              ⚠️ Policy Limit Exceeded
            </p>
            <p className="text-sm text-orange-700 mt-1">
              {expense.policy_warning}
            </p>
          </div>
        )}

        {/* Approval/Rejection Info */}
        {expense.approved_by && (
          <div className="mt-6 overflow-hidden rounded-xl border border-emerald-200/90 bg-emerald-50/60 shadow-sm">
            <div className="flex items-start gap-4 px-5 py-4">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-4 ring-emerald-50"
                aria-hidden
              >
                <FiCheckCircle className="h-6 w-6" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-sm font-medium text-emerald-950">
                  Approved by{" "}
                  <span className="font-semibold">{expense.approved_by?.name}</span>
                </p>
                <p className="mt-1 text-xs text-emerald-800/85">
                  {moment(expense.approved_at).format("DD MMM YYYY, hh:mm A")}
                </p>
                {expense.approval_notes && (
                  <p className="mt-3 rounded-lg border border-emerald-100 bg-white/70 px-3 py-2 text-sm text-emerald-900">
                    <span className="font-medium text-emerald-800">Notes</span>
                    <span className="text-emerald-900/90"> — {expense.approval_notes}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {expense.rejected_by && (
          <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="font-semibold text-red-900">❌ Rejected by {expense.rejected_by?.name}</p>
            <p className="text-sm text-red-700 mt-1">
              {moment(expense.rejected_at).format("DD MMM YYYY, hh:mm A")}
            </p>
            <p className="text-sm text-red-700 mt-2">
              Reason: {expense.rejection_reason}
            </p>
          </div>
        )}

        {expense.returned_by && (
          <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="font-semibold text-orange-900">
              🔄 Returned by {expense.returned_by?.name}
            </p>
            <p className="text-sm text-orange-700 mt-1">
              {moment(expense.returned_at).format("DD MMM YYYY, hh:mm A")}
            </p>
            <p className="text-sm text-orange-700 mt-2">
              Clarification required: {expense.return_reason}
            </p>
          </div>
        )}

        {/* Payment Info */}
        {expense.payment_method && (
          <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="font-semibold text-purple-900">💰 Payment Method</p>
            <p className="text-sm text-purple-700 mt-1">
              {expense.payment_method === "INSTANT_PAYOUT"
                ? "Instant Payout"
                : "Added to Payroll"}
            </p>
            {expense.paid_at && (
              <p className="text-sm text-purple-700 mt-1">
                Paid on: {moment(expense.paid_at).format("DD MMM YYYY")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Activity Log */}
      {activityLog.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Activity Log
          </h3>
          <div className="space-y-4">
            {activityLog.map((activity, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    {activity.action === "CREATED" && <FiUser className="text-primary-600" />}
                    {activity.action === "APPROVED" && "✅"}
                    {activity.action === "REJECTED" && "❌"}
                    {activity.action === "RETURNED" && "🔄"}
                    {activity.action === "PAID" && "💰"}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {activity.description}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    by {activity.user?.name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <FiClock />
                    <span>
                      {moment(activity.created_at).format("DD MMM YYYY, hh:mm A")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Modal for Approve/Reject/Return */}
      <CommonModal
        show={actionModal.show}
        headerTitle={
          actionModal.type === 'approve' 
            ? "Approve Expense" 
            : actionModal.type === 'reject' 
            ? "Reject Expense" 
            : "Return for Clarification"
        }
        onClose={() => setActionModal({ show: false, type: null, notes: "" })}
        modalSize="md"
      >
        <div className="space-y-4 py-4">
          <p className="text-gray-600 text-sm">
            {actionModal.type === 'approve' 
              ? `Approve expense ${expense?.expense_number}?` 
              : actionModal.type === 'reject' 
              ? `Reject expense ${expense?.expense_number}? Please provide reason.`
              : `Return expense ${expense?.expense_number} to employee? Specify what needs clarification.`}
          </p>

          {/* Notes/Reason Input */}
          {(actionModal.type === 'reject' || actionModal.type === 'return') && (
            <DefaultTextarea
              label={
                actionModal.type === 'reject'
                  ? "Rejection Reason *"
                  : "Clarification Required *"
              }
              value={actionModal.notes}
              onChange={(e) =>
                setActionModal((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder={
                actionModal.type === 'reject'
                  ? "Explain why this expense is being rejected..."
                  : "Specify what information is needed..."
              }
              rows={4}
            />
          )}

          {actionModal.type === 'approve' && (
            <DefaultTextarea
              label="Approval Notes (Optional)"
              value={actionModal.notes}
              onChange={(e) =>
                setActionModal((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Add any comments..."
              rows={3}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <DefaultButton
              name="Cancel"
              variant="outline"
              size="md"
              onClick={() => setActionModal({ show: false, type: null, notes: "" })}
              disabled={actionLoading}
            />

            <DefaultButton
              name={
                actionModal.type === 'approve'
                  ? "Approve"
                  : actionModal.type === 'reject'
                  ? "Reject"
                  : "Return"
              }
              variant={
                actionModal.type === 'approve'
                  ? "success"
                  : actionModal.type === 'reject'
                  ? "danger"
                  : "warning"
              }
              size="md"
              loading={actionLoading}
              onClick={handleActionConfirm}
              icon={
                actionModal.type === 'approve' ? (
                  <FiCheck />
                ) : actionModal.type === 'reject' ? (
                  <FiX />
                ) : (
                  <FiRotateCcw />
                )
              }
              classnames="min-w-24"
            />
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default ExpenseDetail;

